import type { AngebotStatus, AuftragStatus } from "@kundeninfo/api-client";

/*
 * Die RÜCKFRAGEN an Vorgängen (Angebot, Auftrag, Rechnung, Positionen). Zuerst die vor einem Statuswechsel an Angebot
 * und Auftrag (Hausregel „Bestätigung vor Datenänderung“, 19.10.2026:
 * kein Statuswechsel per Einzelklick, solange es kein Rückgängig gibt; Hausregel „durchgängig einheitliches Design“,
 * 02.11.2026). Gleiche Form wie „Rechnung … stornieren?“ an der Rechnung (`RechnungDetail.stornieren`): der Titel nennt
 * Vorgang und Zielstatus, der Text, was danach gilt. Rot (`gefaehrlich`) nach der Rot-Regel im Kopf von
 * `useBestaetigung` (@kundeninfo/ui): Ablehnen, Zurückziehen, Stornieren und Löschen. Schlüssel englisch, Werte deutsch (Hausregel „Texte in
 * Properties“).
 *
 * Die Folgen stehen so im Code: Übergänge `offerUebergangErlaubt`/`salesOrderUebergangErlaubt` (domain/salesRules.ts —
 * Angenommen, Abgelehnt, Zurückgezogen, Abgeschlossen und Storniert sind Endzustände); Layout fest beim Verlassen des
 * Entwurfs bzw. des aktiven Auftrags und Anlagen eingefroren bei Gesendet/Zurückgezogen (`setOfferStatus`,
 * `setSalesOrderStatus` in db/repositories); neue Version nach `darfNeueVersion`, Kopieren immer; „Auftrag anlegen“
 * am angenommenen Angebot, „Rechnung vorschlagen“ nur am aktiven Auftrag, „Rechnung erzeugen“ in jedem Status
 * (AuftragDetail.tsx).
 */

/** Die Zielstatus, die ein Knopf am Angebot bzw. Auftrag anbietet (`erlaubteFolgestatus`). */
export type OfferStatusTarget = Exclude<AngebotStatus, "DRAFT" | "SUPERSEDED">;
export type OrderStatusTarget = Exclude<AuftragStatus, "ACTIVE">;

/** Eine Rückfrage, wie `bestaetigen` aus dem Haken `useBestaetigung` sie nimmt. */
export type Question = { titel: string; text: string; bestaetigenText: string; gefaehrlich: boolean };

/** Das Verb auf dem Statusknopf am Auftrag — wie `STATUS_AKTION` am Angebot ein Verb und nicht das Statuswort
 *  („Abgeschlossen“), Hausregel „durchgängig einheitliches Design“; dasselbe Wort steht auf dem Bestätigen-Knopf. */
export const ORDER_STATUS_ACTION: Record<OrderStatusTarget, string> = { COMPLETED: "Abschließen", CANCELLED: "Stornieren" };

const OFFER_CLOSED = "Der Status lässt sich danach nicht mehr ändern; weiter geht es nur mit einer neuen Version "
  + "oder einer Kopie.";
const ORDER_CLOSED = "Status und Layout lassen sich danach nicht mehr ändern; „Rechnung vorschlagen“ entfällt, "
  + "„Rechnung erzeugen“ bleibt für Nachrechnungen.";

export const STATUS_CHANGE_TEXT: {
  offer: Record<OfferStatusTarget, (number: string) => Question>;
  order: Record<OrderStatusTarget, (number: string) => Question>;
} = {
  offer: {
    SENT: (n) => ({
      titel: `Angebot ${n} als gesendet markieren?`,
      text: `Angebot ${n} wird als gesendet markiert — versendet wird nichts. Layout und Anlagen stehen danach fest; `
        + "das Angebot lässt sich dann annehmen, ablehnen oder zurückziehen.",
      bestaetigenText: "Als gesendet markieren", gefaehrlich: false,
    }),
    ACCEPTED: (n) => ({
      titel: `Angebot ${n} annehmen?`,
      text: `Angebot ${n} wird als angenommen markiert. Der Status lässt sich danach nicht mehr ändern; aus dem `
        + "Angebot lässt sich dann ein Auftrag anlegen.",
      bestaetigenText: "Annehmen", gefaehrlich: false,
    }),
    REJECTED: (n) => ({
      titel: `Angebot ${n} ablehnen?`,
      text: `Angebot ${n} wird als abgelehnt markiert. ${OFFER_CLOSED}`,
      bestaetigenText: "Ablehnen", gefaehrlich: true,
    }),
    WITHDRAWN: (n) => ({
      titel: `Angebot ${n} zurückziehen?`,
      text: `Angebot ${n} wird zurückgezogen. ${OFFER_CLOSED}`,
      bestaetigenText: "Zurückziehen", gefaehrlich: true,
    }),
  },
  order: {
    COMPLETED: (n) => ({
      titel: `Auftrag ${n} abschließen?`,
      text: `Auftrag ${n} wird als abgeschlossen markiert. ${ORDER_CLOSED}`,
      bestaetigenText: ORDER_STATUS_ACTION.COMPLETED, gefaehrlich: false,
    }),
    CANCELLED: (n) => ({
      titel: `Auftrag ${n} stornieren?`,
      text: `Auftrag ${n} wird storniert. ${ORDER_CLOSED}`,
      bestaetigenText: ORDER_STATUS_ACTION.CANCELLED, gefaehrlich: true,
    }),
  },
};

/*
 * Runde 2 der Durchsicht (Entscheide 682–684, 02.11.2026) — dieselbe Form wie die Fragen an der Rechnung.
 */

/** „Rechnung erzeugen“ am Auftrag (682) — im Wortlaut wie „Korrekturrechnung anlegen?“ (`RechnungDetail`): Für
 *  Rechnungen gibt es keinen Löschweg. Nicht rot — es entsteht etwas (Rot-Regel: `useBestaetigung`). */
export const CREATE_INVOICE_QUESTION = (orderNumber: string): Question => ({
  titel: "Rechnung erzeugen?",
  text: `Zu Auftrag ${orderNumber} eine Rechnung erzeugen? Sie entsteht als Entwurf mit Kopf, Texten und Positionen `
    + "des Auftrags und lässt sich danach nicht mehr entfernen.",
  bestaetigenText: "Rechnung erzeugen", gefaehrlich: false,
});

/** „PDF in der Akte erneuern“ an Angebot und Rechnung (683) — nur beim Erneuern: Der Server ersetzt den Inhalt der
 *  abgelegten Datei, Kennung und ein von Hand geänderter Name bleiben (`inAkteAblegen`, IDEMPOTENT). Keine
 *  Gefahr-Gestalt — Ersetzen durch einen neuen Stand (Rot-Regel: `useBestaetigung`). */
export const RENEW_FILED_PDF_QUESTION = (fileName: string): Question => ({
  titel: "PDF in der Akte erneuern?",
  text: `Der Inhalt von „${fileName}“ wird durch den aktuellen Stand ersetzt; die Datei behält Namen und Platz in der `
    + "Kundenakte.",
  bestaetigenText: "Erneuern", gefaehrlich: false,
});

/** „Position löschen“ im Positionseditor (684) — rot wie jedes Löschen (Rot-Regel: `useBestaetigung`); der Knopf selbst bleibt leise (Entscheid 43
 *  gilt der Farbe). Einfügen bringt nur eine leere Zeile zurück, nicht Text, Menge und Preis. */
export const DELETE_ITEM_QUESTION = (number: string, name: string): Question => ({
  titel: "Position löschen?",
  text: `Position ${number}${name === "" ? "" : ` „${name}“`} wird gelöscht; zurück kommt sie nur durch neues Einfügen.`,
  bestaetigenText: "Löschen", gefaehrlich: true,
});
